import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  profileRequestResponseSchema,
  profileRequestsResponseSchema,
  userDetailResponseSchema,
  type ProfileRequest,
  type ProfileRequestPatch,
} from '@unit/shared';
import { useAuth } from '../auth/AuthProvider.js';
import { useApi } from '../../lib/useApi.js';
import { apiPost } from '../../lib/api.js';
import { useAction } from '../../lib/useAction.js';
import { ActionError } from '../../components/ActionError.js';
import { ApiError } from '../../components/ApiError.js';
import { Field } from '../../components/Field.js';
import { PersonalDetailsForm } from './PersonalDetailsForm.js';
import { PersonalDetailsRows } from './SoldierDetailPage.js';
import { formatDate } from '../issuance/FormsPage.js';
import { PersonName } from '../../components/PersonName.js';

/**
 * The soldier's own file (REQUIREMENTS §2.6 "My profile"). Read through the
 * API — `canViewUser` allows self — and changed only by request: the form
 * below produces a patch, hr or admin approve it, and the file updates then.
 */
export function MyFilePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const me = useApi(user ? `/users/${user.uid}` : null, userDetailResponseSchema);
  const requests = useApi('/me/profile-requests', profileRequestsResponseSchema);
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState('');
  const [sent, setSent] = useState(false);
  const submit = useAction((patch: ProfileRequestPatch) =>
    apiPost('/me/profile-requests', profileRequestResponseSchema, { patch, note: note.trim() || null }),
  );

  if (me.kind === 'loading') return <p className="text-slate-500">{t('home.loading')}</p>;
  if (me.kind === 'error') return <ApiError code={me.code} requestId={me.requestId} />;
  const detail = me.data.user;
  const pending = requests.kind === 'ready' ? requests.data.requests.find((r) => r.status === 'pending') : undefined;

  return (
    <>
      <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="my-file">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="text-lg font-semibold text-slate-900">
            <PersonName person={detail} />
          </h2>
          {!asking && !pending && (
            <button
              type="button"
              onClick={() => {
                setAsking(true);
                setSent(false);
              }}
              data-testid="request-change"
              className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-700"
            >
              {t('me.requestChange')}
            </button>
          )}
        </div>
        <p className="mt-1 text-sm text-slate-500">{t('me.hint')}</p>
        {pending && (
          <p className="mt-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-200" data-testid="request-pending">
            {t('me.pendingBanner')}
          </p>
        )}
        {sent && (
          <p className="mt-3 text-sm text-emerald-700" data-testid="request-sent">
            {t('me.submitted')}
          </p>
        )}
        <dl className="mt-3 divide-y divide-slate-100">
          <Field label={t('home.personalNumber')} latin>
            {detail.personalNumber}
          </Field>
          <Field label={t('users.email')} latin>
            {detail.email ?? '—'}
          </Field>
        </dl>
        {asking ? (
          <>
            <PersonalDetailsForm
              initial={{ phone: detail.phone, personalDetails: detail.personalDetails }}
              submitLabel={t('me.submit')}
              busy={submit.busy}
              onCancel={() => setAsking(false)}
              onSubmit={(patch) =>
                void submit.run(patch).then((r) => {
                  if (r) {
                    setAsking(false);
                    setSent(true);
                    setNote('');
                    requests.reload();
                  }
                })
              }
            >
              <label className="block text-sm text-slate-700">
                {t('me.note')}
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  data-testid="request-note"
                  className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900 outline-none focus:border-slate-900"
                />
              </label>
            </PersonalDetailsForm>
            <div className="mt-2">
              <ActionError error={submit.error} />
            </div>
          </>
        ) : (
          <PersonalDetailsRows user={detail} withPhone />
        )}
      </section>

      <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="my-requests">
        <h3 className="font-semibold text-slate-900">{t('me.myRequests')}</h3>
        {requests.kind === 'ready' && requests.data.requests.length === 0 && (
          <p className="mt-2 text-sm text-slate-500">{t('me.noRequests')}</p>
        )}
        {requests.kind === 'ready' && (
          <ul className="mt-2 divide-y divide-slate-100">
            {requests.data.requests.map((r) => (
              <RequestRow key={r.id} request={r} />
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function RequestRow({ request }: { request: ProfileRequest }) {
  const { t, i18n } = useTranslation();
  return (
    <li className="py-2 text-sm" data-testid="my-request" data-status={request.status}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="latin-inline text-slate-500">{formatDate(request.requestedAt, i18n.language)}</span>
        <span className="font-medium text-slate-900">{t(`profileRequests.status.${request.status}`)}</span>
      </div>
      <PatchSummary patch={request.patch} />
      {request.reason && <p className="mt-1 text-red-700">{request.reason}</p>}
    </li>
  );
}

/** The requested changes, one line per field, in the reader's language. */
export function PatchSummary({ patch }: { patch: ProfileRequestPatch }) {
  const { t } = useTranslation();
  const lines: [string, string | null][] = [];
  if (patch.phone !== undefined) lines.push([t('users.phone'), patch.phone]);
  const pd = patch.personalDetails ?? {};
  if (pd.dateOfBirth !== undefined) lines.push([t('users.dateOfBirth'), pd.dateOfBirth]);
  if (pd.address !== undefined) lines.push([t('users.address'), pd.address]);
  if (pd.bloodType !== undefined) lines.push([t('users.bloodType'), pd.bloodType]);
  for (const k of ['shirt', 'pants', 'boots', 'vest'] as const) {
    if (pd.sizes?.[k] !== undefined) lines.push([t(`users.sizes.${k}`), pd.sizes[k] ?? null]);
  }
  if (pd.emergencyContact?.name !== undefined) lines.push([t('users.emergencyName'), pd.emergencyContact.name ?? null]);
  if (pd.emergencyContact?.relation !== undefined) lines.push([t('users.emergencyRelation'), pd.emergencyContact.relation ?? null]);
  if (pd.emergencyContact?.phone !== undefined) lines.push([t('users.emergencyPhone'), pd.emergencyContact.phone ?? null]);
  return (
    <ul className="mt-1 text-slate-700" data-testid="patch-summary">
      {lines.map(([label, value]) => (
        <li key={label}>
          {label}: <span className="latin-inline font-medium">{value ?? '—'}</span>
        </li>
      ))}
    </ul>
  );
}
